import React, { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Gauge, RefreshCw, Smartphone, Monitor, Globe, Info, ChevronDown, ChevronRight } from "lucide-react";
import { getStoreId } from "../utils/auth";
import { showToast } from "../utils/toast";
import "./PageSpeedInsights.css";

const apiUrl = import.meta.env.VITE_BACKEND_URL;
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 120000;
const HISTORY_LIMIT = 20;

// Lighthouse category IDs and display order
const CATEGORY_IDS = ["performance", "accessibility", "best-practices", "seo"];
const METRIC_AUDIT_IDS = [
    "first-contentful-paint",
    "largest-contentful-paint",
    "total-blocking-time",
    "speed-index",
    "cumulative-layout-shift",
    "interactive",
];

function scoreColor(score) {
    if (score == null) return "neutral";
    const pct = typeof score === "number" ? score : Math.round((score || 0) * 100);
    if (pct >= 90) return "good";
    if (pct >= 50) return "average";
    return "poor";
}

function formatSavings(audit) {
    if (!audit) return null;
    // Prefer top-level numericValue / numericUnit (e.g. legacy audits)
    let num = audit.numericValue;
    let unit = audit.numericUnit;
    if (num == null && audit.details) {
        if (audit.details.overallSavingsMs != null) {
            num = audit.details.overallSavingsMs;
            unit = "millisecond";
        } else if (audit.details.overallSavingsBytes != null) {
            num = audit.details.overallSavingsBytes;
            unit = "byte";
        }
    }
    if (num == null) return null;
    if (unit === "millisecond") return { text: `${Math.round(num)} ms`, type: "ms" };
    if (unit === "byte" || unit === "bytes") {
        const kb = num / 1024;
        return { text: kb >= 1 ? `${Math.round(kb)} KiB` : `${Math.round(num)} B`, type: "bytes" };
    }
    return null;
}

function PageSpeedInsights({ storeInfo }) {
    const { t } = useTranslation();
    const hashSiteId = getStoreId();
    const defaultUrl = storeInfo?.generalInfo?.storeUrl || "";
    const [url, setUrl] = useState(defaultUrl);
    const [urlOptions, setUrlOptions] = useState([]);
    const [urlOptionsLoading, setUrlOptionsLoading] = useState(false);
    const [strategyTab, setStrategyTab] = useState("mobile");
    const [loading, setLoading] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);
    const [metricsExpanded, setMetricsExpanded] = useState(true);
    const [expandedInsightId, setExpandedInsightId] = useState(null);
    const [howItWorksOpen, setHowItWorksOpen] = useState(false);
    const [selectedRunIndex, setSelectedRunIndex] = useState(0);

    const fetchUrlOptionsInFlightRef = useRef(false);
    const fetchResultsInFlightRef = useRef(false);

    useEffect(() => {
        if (defaultUrl && !url) setUrl(defaultUrl);
    }, [defaultUrl]);

    const fetchUrlOptions = useCallback(async () => {
        if (!hashSiteId) return;
        setUrlOptionsLoading(true);
        try {
            const res = await fetch(`${apiUrl}/api/pagespeed/urls?id=${hashSiteId}`);
            if (!res.ok) return;
            const data = await res.json();
            const options = data.options || [];
            setUrlOptions(options);
            if (data.storeUrl && !url) setUrl(data.storeUrl);
        } catch {
            // ignore
        } finally {
            setUrlOptionsLoading(false);
        }
    }, [hashSiteId]);

    useEffect(() => {
        if (!hashSiteId) return;
        if (fetchUrlOptionsInFlightRef.current) return;
        fetchUrlOptionsInFlightRef.current = true;
        fetchUrlOptions().finally(() => { fetchUrlOptionsInFlightRef.current = false; });
    }, [hashSiteId, fetchUrlOptions]);

    const fetchResults = useCallback(async () => {
        if (!hashSiteId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${apiUrl}/api/pagespeed/results?id=${hashSiteId}&limit=${HISTORY_LIMIT}`);
            if (!res.ok) throw new Error("Failed to load results");
            const data = await res.json();
            setResults(data.results || []);
            setSelectedRunIndex(0);
        } catch (err) {
            setError(err.message);
            setResults([]);
        } finally {
            setLoading(false);
        }
    }, [hashSiteId]);

    useEffect(() => {
        if (!hashSiteId) return;
        if (fetchResultsInFlightRef.current) return;
        fetchResultsInFlightRef.current = true;
        fetchResults().finally(() => { fetchResultsInFlightRef.current = false; });
    }, [hashSiteId, fetchResults]);

    const runAnalyze = async () => {
        if (!hashSiteId) return;
        setError(null);
        setAnalyzing(true);
        const analyzeUrl = (url || "").trim();
        try {
            const res = await fetch(`${apiUrl}/api/pagespeed/analyze?id=${hashSiteId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    url: analyzeUrl || undefined,
                    strategies: ["mobile", "desktop"],
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to queue analysis");
            showToast(t("pageSpeed.analysisQueued"), "info", 6000);
            const jobQueuedAt = Date.now();
            const effectiveUrl = (data.url || analyzeUrl || "").trim();
            const pollStart = Date.now();
            while (Date.now() - pollStart < POLL_TIMEOUT_MS) {
                await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
                const rRes = await fetch(`${apiUrl}/api/pagespeed/results?id=${hashSiteId}&limit=${HISTORY_LIMIT}`);
                const rData = await rRes.json();
                const nextResults = rData.results || [];
                setResults(nextResults);
                const cutoff = jobQueuedAt - 5000;
                const forThisRun = nextResults.filter((r) => {
                    const created = new Date(r.createdAt || 0).getTime();
                    if (created < cutoff) return false;
                    if (effectiveUrl) return (r.url || "").trim() === effectiveUrl;
                    return true;
                });
                const hasMobile = forThisRun.some((r) => String(r.strategy).toLowerCase() === "mobile");
                const hasDesktop = forThisRun.some((r) => String(r.strategy).toLowerCase() === "desktop");
                if (hasMobile && hasDesktop) break;
            }
            await fetchResults();
        } catch (err) {
            setError(err.message);
        } finally {
            setAnalyzing(false);
        }
    };

    // Group flat results into runs: same URL + same time bucket (15 min) so mobile + desktop from one analysis stay together
    const runs = React.useMemo(() => {
        const BUCKET_MS = 15 * 60 * 1000;
        const map = new Map();
        for (const r of results) {
            const ts = r.createdAt ? new Date(r.createdAt).getTime() : 0;
            const bucket = Math.floor(ts / BUCKET_MS) * BUCKET_MS;
            const key = `${(r.url || "").trim()}|${bucket}`;
            if (!map.has(key)) {
                map.set(key, { url: r.url, createdAt: r.createdAt, results: [] });
            }
            map.get(key).results.push(r);
        }
        return Array.from(map.values())
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .map((run) => ({
                ...run,
                createdAt: run.results[0]?.createdAt ?? run.createdAt,
                results: run.results.sort((a, b) => (String(a.strategy).toLowerCase() === "mobile" ? -1 : 1)),
            }));
    }, [results]);

    const displayRun = runs[selectedRunIndex] || runs[0];
    const displayResults = displayRun?.results ?? [];
    const currentResult = displayResults.find((r) => String(r.strategy).toLowerCase() === strategyTab);
    const hasResultForTab = !!currentResult;
    const lh = currentResult?.lighthouseResult || null;
    const audits = lh?.audits || {};
    const categories = lh?.categories || {};

    // Summary scores (Performance, Accessibility, Best Practices, SEO)
    const categoryTitleKey = (id) => (id === "best-practices" ? "bestPractices" : id);
    const summaryScores = CATEGORY_IDS.map((id) => {
        const cat = categories[id];
        const score = cat?.score != null ? Math.round(cat.score * 100) : null;
        const title = cat?.title || t(`pageSpeed.${categoryTitleKey(id)}`);
        return { id, title, score, color: scoreColor(score) };
    });

    const performanceScore = summaryScores.find((s) => s.id === "performance")?.score ?? null;
    const performanceColor = scoreColor(performanceScore);

    // Screenshot from Lighthouse (API may return data: URL or raw base64)
    const rawScreenshot = audits["final-screenshot"]?.details?.data;
    const screenshotSrc = !rawScreenshot ? null : String(rawScreenshot).startsWith("data:")
        ? rawScreenshot
        : `data:image/png;base64,${rawScreenshot}`;

    // Metrics (Core Web Vitals etc.)
    const metricsList = METRIC_AUDIT_IDS.map((id) => {
        const a = audits[id];
        if (!a) return null;
        const score = a.score != null ? Math.round(a.score * 100) : null;
        return {
            id,
            title: a.title,
            displayValue: a.displayValue,
            color: scoreColor(score),
        };
    }).filter(Boolean);

    // Insights: performance auditRefs with group "insights" (matches API categoryGroups)
    const performanceCat = categories.performance;
    const perfAuditRefs = performanceCat?.auditRefs || [];
    const insightsList = perfAuditRefs
        .filter((ref) => ref.group === "insights")
        .map((ref) => {
            const a = audits[ref.id];
            if (!a) return null;
            const savings = formatSavings(a) || (a.displayValue && /savings/i.test(a.displayValue)
                ? { text: a.displayValue.replace(/^Est savings of /i, "").trim(), type: /kiB|bytes/i.test(String(a.displayValue)) ? "bytes" : "ms" }
                : null);
            return {
                id: ref.id,
                title: a.title,
                description: a.description,
                displayValue: a.displayValue,
                savings,
                score: a.score,
                color: a.score == null ? "neutral" : a.score >= 1 ? "good" : a.score < 0.5 ? "poor" : "average",
            };
        })
        .filter(Boolean);

    // Diagnostics: performance auditRefs with group "diagnostics"
    const diagnosticsList = perfAuditRefs
        .filter((ref) => ref.group === "diagnostics")
        .map((ref) => {
            const a = audits[ref.id];
            if (!a) return null;
            const score = a.score != null ? Math.round(a.score * 100) : null;
            const savings = formatSavings(a);
            return {
                id: ref.id,
                title: a.title,
                description: a.description,
                displayValue: a.displayValue,
                savings,
                color: scoreColor(score),
            };
        })
        .filter(Boolean);

    if (!hashSiteId) {
        return (
            <div className="card">
                <div className="card-body text-center py-5 text-muted">
                    {t("pageSpeed.storeIdNotFound")}
                </div>
            </div>
        );
    }

    // Channel dropdown: show selected option's URL; on change replace (not append) url
    const channelValue = urlOptions.some((o) => o.value === url) ? url : (urlOptions[0]?.value ?? "");

    const handleChannelChange = (e) => {
        const v = e.target.value;
        setUrl(v !== undefined && v !== null ? String(v).trim() : "");
    };

    return (
        <>
            <div className="page-speed-insights">
            {/* Top bar: Page Speed title, How it works, Channel */}
            <div className="page-speed-topbar">
                <div className="page-speed-topbar-left">
                    <h1 className="page-speed-title">{t("pageSpeed.pageSpeed")}</h1>
                    <button
                        type="button"
                        className="page-speed-how-it-works"
                        onClick={() => setHowItWorksOpen(!howItWorksOpen)}
                        aria-expanded={howItWorksOpen}
                    >
                        {t("pageSpeed.howItWorks")}
                        <Info size={14} className="ms-1" />
                    </button>
                </div>
                <div className="page-speed-topbar-right">
                    {/* <label className="page-speed-channel-label">
                        <svg className="page-speed-channel-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                        {t("pageSpeed.channel")}
                    </label> */}
                    <select
                        className="page-speed-channel-select"
                        value={channelValue}
                        onChange={handleChannelChange}
                        disabled={analyzing || urlOptionsLoading}
                        aria-label={t("pageSpeed.channel")}
                    >
                        <option value="">{urlOptionsLoading ? t("pageSpeed.loadingUrls") : t("pageSpeed.selectUrl")}</option>
                        {urlOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {howItWorksOpen && (
                <div className="page-speed-how-it-works-panel card border-0 shadow-sm mb-3">
                    <div className="card-body small text-muted">
                        {t("pageSpeed.howItWorksContent")}
                    </div>
                </div>
            )}

            {/* White card: URL + Analyze (no tabs) */}
            <div className="page-speed-input-card card border-0 shadow-sm mb-4">
                <div className="card-body">
                    <div className="page-speed-url-row">
                        <div className="page-speed-url-input-wrap">
                            <Globe size={18} className="page-speed-globe-icon" />
                            <input
                                type="url"
                                className="page-speed-url-input form-control"
                                placeholder={urlOptions[0]?.value || "https://..."}
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                disabled
                                readOnly
                                aria-label={t("pageSpeed.urlLabel")}
                            />
                        </div>
                        <button
                            type="button"
                            className={`page-speed-analyze-btn btn ${analyzing ? "btn-primary page-speed-analyze-btn--analyzing" : "btn-dark"}`}
                            onClick={runAnalyze}
                            disabled={analyzing || !url.trim()}
                        >
                            {analyzing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                    {t("pageSpeed.analyzingMobileDesktop")}
                                </>
                            ) : (
                                t("pageSpeed.analyze")
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {error && <div className="alert alert-danger mb-3">{error}</div>}

            {analyzing && (
                <div className="page-speed-analyzing-card card border-0 shadow-sm mb-4">
                    <div className="card-body text-center py-5">
                        <div className="page-speed-running-analysis-icon" aria-hidden="true">
                            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="4" strokeOpacity="0.2" fill="none" />
                                <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round"
                                    strokeDasharray="88 264" strokeDashoffset="22"
                                    className="page-speed-running-analysis-arc" />
                            </svg>
                        </div>
                        <p className="mb-0 text-muted fw-medium">{t("pageSpeed.runningAnalysis")}</p>
                    </div>
                </div>
            )}

            {!currentResult && !analyzing && results.length === 0 && (
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5 text-muted">
                        {t("pageSpeed.noResults")}
                    </div>
                </div>
            )}

            {!analyzing && displayRun && (
                <>
                    {/* Device selector: Mobile | Desktop | Run date + Refresh (right) */}
                    <div className="page-speed-device-bar mb-4">
                        <div className="page-speed-device-tabs" role="tablist" aria-label={t("pageSpeed.deviceTablist")}>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={strategyTab === "mobile"}
                                aria-controls="page-speed-results-panel"
                                id="page-speed-tab-mobile"
                                className={`page-speed-device-tab ${strategyTab === "mobile" ? "active" : ""}`}
                                onClick={() => setStrategyTab("mobile")}
                            >
                                <Smartphone size={20} className="me-2" />
                                {t("pageSpeed.mobile")}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={strategyTab === "desktop"}
                                aria-controls="page-speed-results-panel"
                                id="page-speed-tab-desktop"
                                className={`page-speed-device-tab ${strategyTab === "desktop" ? "active" : ""}`}
                                onClick={() => setStrategyTab("desktop")}
                            >
                                <Monitor size={20} className="me-2" />
                                {t("pageSpeed.desktop")}
                            </button>
                        </div>
                        <div className="page-speed-device-bar-right">
                            <span className="page-speed-run-tag">
                                {t("pageSpeed.runAt", {
                                    date: displayRun?.createdAt
                                        ? new Date(displayRun.createdAt).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
                                        : "—",
                                })}
                            </span>
                            <button
                                type="button"
                                className="page-speed-refresh-btn btn btn-outline-secondary btn-sm"
                                onClick={fetchResults}
                                disabled={loading}
                            >
                                {loading ? <span className="spinner-border spinner-border-sm me-1" role="status" /> : <RefreshCw size={16} className="me-1" />}
                                {t("common.refresh")}
                            </button>
                        </div>
                    </div>

                    {/* Previous runs – dropdown to select a run */}
                    {runs.length > 1 && (
                        <div className="page-speed-history page-speed-history--dropdown mb-4">
                            <label className="page-speed-history-label" htmlFor="page-speed-run-select">
                                {t("pageSpeed.runHistory")}
                            </label>
                            <select
                                id="page-speed-run-select"
                                className="page-speed-history-select form-select"
                                value={selectedRunIndex}
                                onChange={(e) => setSelectedRunIndex(parseInt(e.target.value, 10))}
                                aria-label={t("pageSpeed.runHistory")}
                            >
                                {runs.map((run, idx) => (
                                    <option key={`${run.url}|${run.createdAt}`} value={idx}>
                                        {run.createdAt
                                            ? new Date(run.createdAt).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
                                            : "—"}
                                        {" — "}
                                        {run.url ? (run.url.length > 50 ? run.url.slice(0, 47) + "…" : run.url) : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Diagnose performance issues – compact (key forces re-render when strategy changes) */}
                    <div key={`run-${selectedRunIndex}-${strategyTab}`} id="page-speed-results-panel" role="tabpanel" aria-labelledby={`page-speed-tab-${strategyTab}`} className="page-speed-results-panel">
                    {!hasResultForTab ? (
                        <div className="card border-0 shadow-sm mb-4">
                            <div className="card-body text-center py-5 text-muted">
                                {t("pageSpeed.noResultForTab", { tab: strategyTab === "mobile" ? t("pageSpeed.mobile") : t("pageSpeed.desktop") })}
                            </div>
                        </div>
                    ) : (
                    <div className="page-speed-diagnose-card page-speed-diagnose-card--compact card border-0 shadow-sm mb-4">
                        <div className="card-body page-speed-diagnose-card-body">
                            <div className="page-speed-diagnose-header">
                                <div className="page-speed-diagnose-icon">
                                    <Gauge size={24} />
                                </div>
                                <h2 className="page-speed-diagnose-title">{t("pageSpeed.diagnoseTitle")}</h2>
                            </div>
                            {displayRun?.url && (
                                <div className="page-speed-analyzed-url">
                                    <span className="page-speed-analyzed-url-label">{t("pageSpeed.analyzedUrl")}</span>
                                    <a href={displayRun.url} target="_blank" rel="noopener noreferrer" className="page-speed-analyzed-url-link" title={displayRun.url}>
                                        {displayRun.url}
                                    </a>
                                </div>
                            )}

                            {/* Summary score circles */}
                            <div className="page-speed-summary-scores mb-4">
                                {summaryScores.map(({ id, title, score, color }) => (
                                    <div key={id} className="page-speed-summary-score">
                                        <div className={`page-speed-gauge page-speed-gauge--circle page-speed-gauge--${color}`}>
                                            <span>{score != null ? score : "—"}</span>
                                        </div>
                                        <div className="page-speed-summary-label">{title}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="page-speed-main-row">
                                {/* Large Performance gauge + legend */}
                                <div className="page-speed-gauge-section">
                                    <div className={`page-speed-gauge-large page-speed-gauge--${performanceColor}`}>
                                        <span className="page-speed-gauge-large-value">{performanceScore != null ? performanceScore : "—"}</span>
                                    </div>
                                    <div className="page-speed-gauge-label">{t("pageSpeed.performance")}</div>
                                    <p className="page-speed-score-note small text-muted mt-2 mb-2">
                                        {t("pageSpeed.scoreNote")}{" "}
                                        <a href="https://developer.chrome.com/docs/lighthouse/performance/performance-scoring/" target="_blank" rel="noopener noreferrer" className="text-primary">{t("pageSpeed.performanceScore")}</a>
                                        {" "}{t("pageSpeed.seeCalculator")}{" "}
                                        <a href="https://developer.chrome.com/docs/lighthouse/performance/performance-scoring/#calculator" target="_blank" rel="noopener noreferrer" className="text-primary">{t("pageSpeed.seeCalculatorLink")}</a>.
                                    </p>
                                    <div className="page-speed-legend">
                                        <span className="page-speed-legend-item page-speed-legend--poor">0–49</span>
                                        <span className="page-speed-legend-item page-speed-legend--average">50–89</span>
                                        <span className="page-speed-legend-item page-speed-legend--good">90–100</span>
                                    </div>
                                </div>

                                {/* Screenshot */}
                                {screenshotSrc && (
                                    <div className="page-speed-screenshot-wrap">
                                        <div className="page-speed-screenshot-frame">
                                            <img src={screenshotSrc} alt="" className="page-speed-screenshot-img" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* METRICS */}
                            <div className="page-speed-metrics-section mt-4">
                                <button
                                    type="button"
                                    className="page-speed-section-heading"
                                    onClick={() => setMetricsExpanded(!metricsExpanded)}
                                    aria-expanded={metricsExpanded}
                                >
                                    <span className="page-speed-section-title">{t("pageSpeed.metrics")}</span>
                                    <span className="page-speed-section-action">{metricsExpanded ? t("pageSpeed.collapseView") : t("pageSpeed.expandView")}</span>
                                    {metricsExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                </button>
                                {metricsExpanded && (
                                    <div className="page-speed-metrics-grid">
                                        {metricsList.map((m) => (
                                            <div key={m.id} className="page-speed-metric-row">
                                                <span className={`page-speed-dot page-speed-dot--${m.color}`} />
                                                <span className="page-speed-metric-title">{m.title}</span>
                                                <span className="page-speed-metric-value">{m.displayValue || "—"}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    )}
                    </div>
                </>
            )}
        </div>
        </>
    );
}

export default PageSpeedInsights;
